import { useRef, useState, useEffect } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { Divider, keyframes } from '@mui/material';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

const formatNotifTypeName = (type, t) => {
  const str = String(type || 'Alert');
  if (str.toLowerCase().includes('enter')) return t ? t('notification.enterLocation', 'Enter Location') : 'Enter Location';
  if (str.toLowerCase().includes('exit')) return t ? t('notification.exitLocation', 'Exit Location') : 'Exit Location';
  if (str.toLowerCase().includes('breaking')) return t ? t('notification.harshBraking', 'Harsh Braking') : 'Harsh Braking';
  return str;
};

const getNotifTypeColor = (type) => {
  const t = String(type).toLowerCase();
  if (t.includes('over speed') || t.includes('panic') || t.includes('danger') || t.includes('drop power volt') || t.includes('low battery'))
    return 'error.main';
  if (t.includes('harsh')) return 'error.main';
  if (t.includes('under')) return 'warning.main';
  if (t.includes('enter')) return 'success.main';
  if (t.includes('exit')) return 'primary.main';
  if (t.includes('door') || t.includes('bad gsm') || t.includes('bad position')) return 'warning.main';
  return 'text.primary';
};

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Import Custom Hook & Component
import { useAlertNotifications } from '../../../../stores/alertNotificationStore';
import NotificationDialog from './NotificationDialog';
import { useLanguage } from '../../../../utils/i18n';
import useHeaderTheme from 'hooks/useHeaderTheme';

const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

// ==============================|| HELPER FUNCTIONS ||============================== //

const getStatusColor = (status) => {
  const s = String(status).toLowerCase();
  if (s === 'danger' || s === 'expired') return 'error';
  if (s === 'warning') return 'warning';
  return 'success';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d
    .toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    .replace(/\./g, ':');
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const { t } = useLanguage();
  const { iconColor, hoverColor, badgeBg, badgeColor, badgeClash } = useHeaderTheme();
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [companyId, setCompanyId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false); // STATE UNTUK DIALOG

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const storedId = localStorage.getItem('companyId');
        if (storedId) {
          setCompanyId(storedId);
        }
      } catch (error) {
        console.error('Gagal mengambil companyId dari localStorage', error);
      }
    }
  }, []);

  const { alerts, docsAlerts, totalUnread, isLoading } = useAlertNotifications(companyId, true);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = () => {
    setOpen(false); // Tutup popover kecil
    setDialogOpen(true); // Buka dialog besar
  };

  // ==============================|| ANIMATIONS ||============================== //

  const shake = keyframes`
  0% { transform: rotate(0deg) scale(1); }
  10% { transform: rotate(-15deg) scale(1.1); }
  20% { transform: rotate(15deg) scale(1.1); }
  30% { transform: rotate(-15deg) scale(1.1); }
  40% { transform: rotate(15deg) scale(1.1); }
  50% { transform: rotate(0deg) scale(1); }
  100% { transform: rotate(0deg) scale(1); }
`;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: iconColor,
          bgcolor: open ? hoverColor : 'transparent',
          '&:hover': { bgcolor: hoverColor },
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge
          badgeContent={totalUnread}
          max={99}
          sx={{
            animation: totalUnread > 0 ? `${shake} 1.5s infinite ease-in-out` : 'none',
            display: 'inline-flex',
            '& .MuiBadge-badge': {
              ...(badgeBg && { backgroundColor: badgeBg }),
              ...(badgeColor && { color: badgeColor }),
              ...(badgeClash && {
                border: '2px solid #ffffff',
                boxShadow: '0 0 8px rgba(255, 255, 255, 0.6)'
              })
            }
          }}
        >
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows.z1,
                width: 420,
                overflow: 'hidden'
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard title={t('notification.title', 'Notifikasi')} elevation={0} border={false} content={false}>
                  {/* TAB PILIHAN */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ minHeight: 40 }}>
                      <Tab
                        label={
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <WarningAmberRoundedIcon fontSize="small" /> {t('notification.general', 'General')} ({alerts.length})
                          </span>
                        }
                        sx={{ textTransform: 'none', minHeight: 40, py: 1 }}
                      />
                      <Tab
                        label={
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <DescriptionOutlinedIcon fontSize="small" /> {t('notification.document', 'Dokumen')} ({docsAlerts.length})
                          </span>
                        }
                        sx={{ textTransform: 'none', minHeight: 40, py: 1 }}
                      />
                    </Tabs>
                  </Box>

                  {/* KONTEN LIST (SCROLLABLE) */}
                  <Box sx={{ flexGrow: 1, maxHeight: 300, overflowY: 'auto' }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                      </Box>
                    ) : (
                      <>
                        {/* TAB 0: GENERAL ALERTS */}
                        {activeTab === 0 && (
                          <List
                            component="nav"
                            sx={{
                              p: 0,
                              '& .MuiListItemButton-root': {
                                py: 1.5,
                                px: 2,
                                '&:hover': { bgcolor: 'grey.50' },
                                '& .MuiAvatar-root': avatarSX,
                                '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                              }
                            }}
                          >
                            {alerts.length === 0 ? (
                              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                                <Typography variant="body2">
                                  {t('notification.noNewVehicleAlerts', 'Tidak ada alert kendaraan baru.')}
                                </Typography>
                              </Box>
                            ) : (
                              alerts.map((alert, i) => (
                                <ListItemButton key={alert.id || i} divider>
                                  <ListItemAvatar>
                                    <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                                      <DirectionsCarFilledOutlinedIcon />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="subtitle1">
                                          {alert.license_plate || t('notification.unknownVehicle', 'Unknown Vehicle')}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5 }}>
                                          <Box
                                            component="span"
                                            sx={{ fontWeight: 'bold', mr: 1, color: getNotifTypeColor(alert.notif_type) }}
                                          >
                                            {formatNotifTypeName(alert.notif_type, t)}
                                          </Box>
                                          {t('notification.detected', 'terdeteksi.')}
                                          {alert.speed ? ` ${t('notification.speed', 'Kecepatan:')} ${alert.speed} km/h.` : ''}
                                          {alert.location ? ` ${t('notification.location', 'Lokasi:')} ${alert.location}.` : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatDate(alert.finish_time)}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItemButton>
                              ))
                            )}
                          </List>
                        )}

                        {/* TAB 1: DOCUMENT ALERTS */}
                        {activeTab === 1 && (
                          <List
                            component="nav"
                            sx={{
                              p: 0,
                              '& .MuiListItemButton-root': {
                                py: 1.5,
                                px: 2,
                                '&:hover': { bgcolor: 'grey.50' },
                                '& .MuiAvatar-root': avatarSX,
                                '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                              }
                            }}
                          >
                            {docsAlerts.length === 0 ? (
                              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                                <Typography variant="body2">{t('notification.allDocsSafe', 'Semua dokumen aman & berlaku.')}</Typography>
                              </Box>
                            ) : (
                              docsAlerts.map((doc, i) => {
                                const isDanger = doc.statusNotif?.toLowerCase() === 'danger';
                                return (
                                  <ListItemButton key={i} divider>
                                    <ListItemAvatar>
                                      <Avatar
                                        sx={{
                                          color: isDanger ? 'error.main' : 'warning.main',
                                          bgcolor: isDanger ? 'error.lighter' : 'warning.lighter'
                                        }}
                                      >
                                        <DescriptionOutlinedIcon />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                          <Typography variant="subtitle1" noWrap sx={{ maxWidth: 180 }}>
                                            {doc.name || t('notification.document', 'Dokumen')}
                                          </Typography>
                                          <Chip
                                            label={doc.statusNotif || t('notification.expired', 'Expired')}
                                            color={getStatusColor(doc.statusNotif)}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                          />
                                        </Box>
                                      }
                                      secondary={
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                          <Typography variant="caption" sx={{ color: 'text.primary' }}>
                                            <b>{doc.notifName}</b> {doc.licenseNo ? `(${doc.licenseNo})` : ''}
                                          </Typography>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              Exp: {doc.expDate}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{ fontWeight: 'bold', color: isDanger ? '#d32f2f' : '#ed6c02' }}
                                            >
                                              {doc.dayLeft < 0
                                                ? `${t('notification.overdue', 'Terlewat')} ${Math.abs(doc.dayLeft)} ${t('notification.days', 'Hari')}`
                                                : `${t('notification.remaining', 'Sisa')} ${doc.dayLeft} ${t('notification.days', 'Hari')}`}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      }
                                    />
                                  </ListItemButton>
                                );
                              })
                            )}
                          </List>
                        )}
                      </>
                    )}
                  </Box>

                  <Divider />
                  {/* TOMBOL VIEW ALL MENGARAH KE DIALOG */}
                  <ListItemButton sx={{ textAlign: 'center', py: 1.5, justifyContent: 'center' }} onClick={handleOpenDialog}>
                    <Typography variant="h6" color="primary">
                      {t('notification.viewAll', 'View All Notifications')}
                    </Typography>
                  </ListItemButton>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* RENDER DIALOG */}
      <NotificationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        alerts={alerts}
        docsAlerts={docsAlerts}
        initialTab={activeTab}
      />
    </Box>
  );
}
